<template>
	<BarArrowButton :large="true" :flex="true" :title="this.title ? this.titleLabel : null">
		<v-row class="barArrowButtonSlotContent">
			<v-col class="centered" v-if="!selectedTotals">
				{{ waitMessage }}
				<v-progress-linear height="2px" indeterminate></v-progress-linear>
			</v-col>
		</v-row>
		<div :class="'barArrowButtonSlotContent ' + (small ? 'small' : '')" v-if="selectedTotals">
			<v-row>
				<v-chart
					v-if="chartOption"
					ref="spendChart"
					:option="chartOption"
					:style="{ height: chartHeight + 'px', width: '100%' }"
					autoresize
				/>
			</v-row>
			<v-row class="labelRow">
				<v-col class="labelCol centered">In: {{ formatter.format(Math.round(totalIn), false, false).replace('-', '') }}</v-col>
				<v-col class="labelCol centered">Pay: {{ this.payIn() }} </v-col>
				<v-col class="labelCol centered">Out: {{ formatter.format(Math.round(totalOut), false, false).replace('-', '') }}</v-col>
			</v-row>
		</div>
	</BarArrowButton>
</template>

<script>
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { BarChart } from 'echarts/charts'
import { GridComponent, TooltipComponent } from 'echarts/components'
import VChart from 'vue-echarts'
import { GRANDPARENT_CLASSIFICATIONS } from '../../mixins/SpendingAnalysis';
import BarArrowButton from './BarArrowButton.vue';

// Tree-shake: register only what we need
use([CanvasRenderer, BarChart, GridComponent, TooltipComponent])

export default {
	name: "AnalysisWidget",
	components: { VChart, BarArrowButton },
	props: {
		spendingAnalysisStructured: {
			type: Object,
			required: true
		},
		selectedSlotKey: { type: String, required: true },
		fakeTotals: { type: Boolean, required: false, default: false },
		formatter: { type: Object, required: true },
		title: { type: Boolean, required: false, default: false },
		titleLabel: { type: String, required: false, default: 'Spending Analysis' },
		titleSize: { type: String, required: false, default: 'normal' }, // normal | small
		waitMessage: { type: String, required: false, default: 'Loading...' },
		chartHeight: { type: Number, required: false, default: 150 },
		small: { type: Boolean, required: false, default: false },
	},
	data() {
		return {
			allEveryDaySpend: [],
			allCommittedCosts: [],
			allFuturePlans: [],
			allIncome: [],
			selectedTotals: null,
			titleFontSize: '20px',
			totalIn: 0,
			totalOut: 0
		}
	},
	computed: {
		chartOption() {
			if (!this.selectedTotals || this.selectedTotals.length === 0) return null;

			const income = this.selectedTotals[GRANDPARENT_CLASSIFICATIONS.INCOME].Amount;
			const fixedCosts = this.selectedTotals[GRANDPARENT_CLASSIFICATIONS.FIXED_COSTS].Amount;
			const everyDaySpend = this.selectedTotals[GRANDPARENT_CLASSIFICATIONS.EVERYDAY_SPEND].Amount;
			const futurePlanning = this.selectedTotals[GRANDPARENT_CLASSIFICATIONS.FUTURE_PLANNING].Amount;

			// Determine which is the topmost non-zero segment in the spend stack
			const roundFuturePlanning = futurePlanning > 0;
			const roundEveryDaySpend = !roundFuturePlanning && everyDaySpend > 0;
			const roundFixedCosts = !roundFuturePlanning && !roundEveryDaySpend && fixedCosts > 0;

			return {
				animation: true,
				grid: {
					left: '3%',
					right: '4%',
					bottom: '8%',
					top: '10%',
					containLabel: true
				},
				xAxis: {
					type: 'category',
					data: ['Income', 'Spend'],
					axisLine: { show: false },
					axisTick: { show: false },
					axisLabel: { show: false }
				},
				yAxis: {
					type: 'value',
					axisLine: {
						show: true,
						lineStyle: { color: '#e0e0e0' }
					},
					splitLine: {
						show: true,
						lineStyle: { color: '#e0e0e0' }
					},
					splitNumber: 3,
					minInterval: 5000,
					axisLabel: {
						show: true,
						color: '#000000',
						formatter: (value) => {
							if (value >= 1000) {
								return this.formatter.format(Math.round(value / 1000), true, false) + 'k';
							}
							return this.formatter.format(value, true, false);
						}
					}
				},
				series: [
					{
						name: 'Income',
						type: 'bar',
						stack: 'total',
						data: [income, 0],
						silent: true,
						itemStyle: {
							color: '#B4D8D7',
							borderRadius: [10, 10, 0, 0]
						},
						barMaxWidth: 110
					},
					{
						name: 'Fixed Costs',
						type: 'bar',
						stack: 'total',
						data: [0, fixedCosts],
						silent: true,
						itemStyle: {
							color: '#413655',
							borderRadius: (roundFixedCosts) ? [10, 10, 0, 0] : 0
						},
						barMaxWidth: 110
					},
					{
						name: 'Everyday Spend',
						type: 'bar',
						stack: 'total',
						data: [0, everyDaySpend],
						silent: true,
						itemStyle: {
							color: '#E3E6FA',
							borderRadius: (roundEveryDaySpend) ? [10, 10, 0, 0] : 0
						},
						barMaxWidth: 110
					},
					{
						name: 'Future Planning',
						type: 'bar',
						stack: 'total',
						data: [0, futurePlanning],
						silent: true,
						itemStyle: {
							color: '#F0D8CF',
							borderRadius: (roundFuturePlanning) ? [10, 10, 0, 0] : 0
						},
						barMaxWidth: 110
					}
				],
			};
		}
	},
	watch: {
		spendingAnalysisStructured: {
			immediate: true,
			handler(newVal) {
				if (newVal && newVal.grandparentAnalysis) {
					this.getStructuredCategories();
				}
			}
		},
		selectedSlotKey: {
			handler(newVal) {
				if (newVal && this.allIncome.length > 0) { 
					this.updateSelectedTotals();
					this.updateChart();
				}
			}
		}
	},
	methods: {
		onLoad() {
			this.getStructuredCategories();
			if (this.titleSize === 'small') {
				this.titleFontSize = '16px';
			}
		},
		payIn() {
			if (this.fakeTotals) {
				return '25 days';
			}
			var daysRemaining = this.spendingAnalysisStructured.daysTillCycleResets ? this.spendingAnalysisStructured.daysTillCycleResets : 0;
			var pluralLabel = daysRemaining === 1 ? 'day' : 'days';
			return daysRemaining + ' ' + pluralLabel;
		},
		getStructuredCategories() {
			if (this.fakeTotals) {
				this.allEveryDaySpend = [];
				this.allCommittedCosts = [];
				this.allFuturePlans = [];
				this.allIncome = [];
				this.selectedTotals = this.getMockSpendingTotals();
				this.updateChart();
				return;
			}
			this.allEveryDaySpend = this.spendingAnalysisStructured.grandparentAnalysis.filter(item => item.grandparent === GRANDPARENT_CLASSIFICATIONS.EVERYDAY_SPEND);
			this.allCommittedCosts = this.spendingAnalysisStructured.grandparentAnalysis.filter(item => item.grandparent === GRANDPARENT_CLASSIFICATIONS.FIXED_COSTS);
			this.allFuturePlans = this.spendingAnalysisStructured.grandparentAnalysis.filter(item => item.grandparent === GRANDPARENT_CLASSIFICATIONS.FUTURE_PLANNING);
			this.allIncome = this.spendingAnalysisStructured.grandparentAnalysis.filter(item => item.grandparent === GRANDPARENT_CLASSIFICATIONS.INCOME);
			
			if (this.selectedSlotKey) {
				this.updateSelectedTotals();
				this.updateChart();
			}
		},
		updateSelectedTotals() {
			this.selectedTotals = [];
			
			// Calculate selectedIncome
			var selectedIncome = this.allIncome.find(s => s.slotName === this.selectedSlotKey);
			if (!selectedIncome) {
				selectedIncome = { totalIn: 0, totalOut: 0, totalNet: 0, groupedAnalysis: [] };
			}

			// Calculate other categories using simplified logic
			var selectedFuturePlans = this.getSelectedCategoryForTotals(this.allFuturePlans);
			var selectedEverydaySpend = this.getSelectedCategoryForTotals(this.allEveryDaySpend);
			var selectedCommittedCosts = this.getSelectedCategoryForTotals(this.allCommittedCosts);

			this.selectedTotals.push({
				"id": GRANDPARENT_CLASSIFICATIONS.INCOME, 
				"Amount": Math.round(Math.abs(selectedIncome.totalIn))
			});
			this.selectedTotals.push({
				"id": GRANDPARENT_CLASSIFICATIONS.FUTURE_PLANNING,
				"Amount": Math.round(Math.abs(selectedFuturePlans.totalNet))
			});
			this.selectedTotals.push({
				"id": GRANDPARENT_CLASSIFICATIONS.EVERYDAY_SPEND,
				"Amount": Math.round(Math.abs(selectedEverydaySpend.totalNet))
			});
			this.selectedTotals.push({
				"id": GRANDPARENT_CLASSIFICATIONS.FIXED_COSTS,
				"Amount": Math.round(Math.abs(selectedCommittedCosts.totalNet))
			});
		},
		updateChart() {
			if (!this.selectedTotals || this.selectedTotals.length === 0) return;

			var income = this.selectedTotals[GRANDPARENT_CLASSIFICATIONS.INCOME].Amount;
			var fixedCosts = this.selectedTotals[GRANDPARENT_CLASSIFICATIONS.FIXED_COSTS].Amount;
			var everyDaySpend = this.selectedTotals[GRANDPARENT_CLASSIFICATIONS.EVERYDAY_SPEND].Amount;
			var futurePlanning = this.selectedTotals[GRANDPARENT_CLASSIFICATIONS.FUTURE_PLANNING].Amount;

			this.totalIn = income;
			this.totalOut = fixedCosts + everyDaySpend + futurePlanning;

			// No need to manually update chart - computed property handles it
		},
		getSelectedCategoryForTotals(categoryArray) {
			var selected = categoryArray.find(s => s.slotName === this.selectedSlotKey);
			if (!selected) {
				selected = { totalIn: 0, totalOut: 0, totalNet: 0, groupedAnalysis: [] };
			}
			return selected;
		},
		getMockSpendingTotals() {
			var totals = [];
			totals.push(
				{
					"id": GRANDPARENT_CLASSIFICATIONS.INCOME, 
					"Amount": Math.round(2000)
				});
			totals.push(
				{
					"id": GRANDPARENT_CLASSIFICATIONS.FUTURE_PLANNING,
					"Amount": Math.round(100)
				});
			totals.push(
				{
					"id": GRANDPARENT_CLASSIFICATIONS.EVERYDAY_SPEND,
					"Amount": Math.round(400)
				});
			totals.push(
				{
					"id": GRANDPARENT_CLASSIFICATIONS.FIXED_COSTS,
					"Amount": Math.round(900)
				});
			return totals;
		},
	},
	mounted: function() { this.onLoad(); }
}
</script>

<style scoped>

.barArrowButtonSlotContent {
	padding: 7px;
	padding-top: 12px;
	padding-bottom: 5px;
}

.title-col {
	font-size: 20px;
	white-space: nowrap;
	margin-top: auto;
	margin-bottom: auto;
}

.arrow-col {
	text-align: right;
	padding-left: 0px;
	margin-top: auto;
	margin-bottom: auto;
}

.arrow {
	height: 10px;
}

.labelRow, .labelCol {
	padding-top: 0px;
}

.small {
	font-size: 9px !important;
}

</style>